import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, Cpu, Rocket, BookOpen, MapPin, Building2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TEAM_IMAGES = {
  founder: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxidXNpbmVzc21hbiUyMHN1aXR8ZW58MHx8fHwxNzkwODAzMjEzfDA&ixlib=rb-4.1.0&q=85',
  cmo: 'https://images.unsplash.com/photo-1623880840102-7df0a9f3545b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxidXNpbmVzc21hbiUyMHN1aXR8ZW58MHx8fHwxNzkwODAzMjEzfDA&ixlib=rb-4.1.0&q=85',
  cfo: 'https://images.unsplash.com/photo-1637684666772-1f215bfd0f5d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw0fHxDRU8lMjBwb3J0cmFpdHxlbnwwfHx8fDE3OTA4MDMyMTN8MA&ixlib=rb-4.1.0&q=85',
  cto: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxleGVjdXRpdmUlMjBoZWFkc2hvdHxlbnwwfHx8fDE3OTA4MDMyMDR8MA&ixlib=rb-4.1.0&q=85',
  office: 'https://images.unsplash.com/photo-1551649081-39f820bb2d3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBvZmZpY2V8ZW58MHx8fHwxNzkwODAzMjA0fDA&ixlib=rb-4.1.0&q=85',
};

const CAPABILITIES = [
  { icon: Briefcase, title: 'Financial Consulting & Investment', desc: 'Wealth building and corporate advisory.' },
  { icon: Cpu, title: 'IT & Technology Consulting', desc: 'Tailored tech frameworks driven by our expert engineering team.' },
  { icon: Rocket, title: 'Startup Funding', desc: 'VC funding and entrepreneurial facilitation.' },
  { icon: BookOpen, title: 'Book Publishing', desc: 'Turning ideas into reality for authors globally.' },
];

const TEAM = [
  { name: 'Sayed Mid', role: 'Founder & CEO', bio: 'Driving the global vision, investment strategies, and overarching mission of Midbrothers.', img: TEAM_IMAGES.founder },
  { name: 'Andrea Bivona', role: 'Chief Marketing Officer (CMO)', bio: 'Architecting our brand presence, target marketing strategies, and operational change management frameworks.', img: TEAM_IMAGES.cmo },
  { name: 'Sahed Ahmed', role: 'Chief Financial Officer (CFO)', bio: 'Leading our corporate financial advisory, asset management, and deal structuring operations.', img: TEAM_IMAGES.cfo },
  { name: 'Abu Sufian', role: 'Chief Technology Officer (CTO)', bio: 'Spearheading IT consulting, technological innovation, and engineering full-stack.', img: TEAM_IMAGES.cto },
];

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-[#0a2c4d] overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ backgroundImage: `url(${TEAM_IMAGES.office})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a2c4d] via-[#0a2c4d]/95 to-[#0a3b6a]/85" />
        <div className="relative max-w-[1280px] mx-auto px-6 py-28 md:py-36">
          <p className="eyebrow text-white/70 mb-4">ABOUT MIDBROTHERS</p>
          <h1 className="font-serif-display text-white text-5xl md:text-7xl font-bold leading-[1.02] mb-8 max-w-4xl">
            Innovate <br className="hidden md:block" /> Beyond Borders.
          </h1>
          <p className="text-white/85 text-lg md:text-xl leading-relaxed max-w-3xl mb-5">
            Midbrothers is a global financial technology and consulting firm that enables businesses and individuals
            to scale, invest, and innovate through a single, unified partnership.
          </p>
          <p className="text-white/75 text-base md:text-lg leading-relaxed max-w-3xl">
            By connecting expert financial strategies, cutting-edge IT consulting, and venture capital into one
            comprehensive ecosystem, Midbrothers gives businesses the speed and capabilities to grow in any market.
          </p>
        </div>
      </section>

      {/* Large callout */}
      <section className="py-24 bg-white">
        <div className="max-w-[1180px] mx-auto px-6">
          <h2 className="font-serif-display text-[#0a2c4d] text-3xl md:text-5xl font-bold leading-[1.1] mb-8">
            They told you to choose between finance, tech, and creativity.
            <span className="text-[#0a6cb0]"> We built a company that does it all.</span>
          </h2>
          <p className="text-[#556a85] text-lg leading-relaxed mb-14 max-w-4xl">
            We build the strategic infrastructure that powers growth for any business, anywhere. Whether you are
            scaling a fintech startup, seeking venture capital, deploying enterprise IT solutions, or publishing a
            book, Midbrothers provides the blueprint and the build.
          </p>

          <div className="grid md:grid-cols-2 gap-5">
            {CAPABILITIES.map((c) => (
              <div key={c.title} className="flex gap-5 p-6 rounded-xl border border-[#eef1f5] hover:border-[#0a6cb0] hover:shadow-[0_14px_30px_-18px_rgba(10,108,176,0.35)] transition-all bg-[#f9fbfd]">
                <div className="service-icon-wrap shrink-0"><c.icon size={26} /></div>
                <div>
                  <h3 className="font-bold text-[#0a2c4d] text-lg mb-1">{c.title}</h3>
                  <p className="text-[#556a85] text-[15px] leading-relaxed">{c.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our History */}
      <section className="py-24 bg-[#f6f9fc]">
        <div className="max-w-[1280px] mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img src={TEAM_IMAGES.office} alt="Midbrothers office" className="w-full h-[500px] object-cover rounded-2xl shadow-xl" />
            <div className="absolute -bottom-6 left-6 right-6 md:right-auto md:w-72 bg-white rounded-2xl shadow-xl p-6 border border-[#eef1f5]">
              <div className="flex items-center gap-3 mb-3">
                <Building2 className="text-[#0a6cb0]" size={22} />
                <p className="font-bold text-[#0a2c4d]">Global Presence</p>
              </div>
              <div className="text-sm text-[#556a85] space-y-2">
                <div className="flex items-center gap-2"><MapPin size={14} className="text-[#0a6cb0]" /><span><b className="text-[#0a2c4d]">Rome</b> — Operational HQ</span></div>
                <div className="flex items-center gap-2"><MapPin size={14} className="text-[#0a6cb0]" /><span><b className="text-[#0a2c4d]">London</b> — Registered Office</span></div>
                <div className="pt-2 border-t border-[#eef1f5] text-xs text-[#8ea1b8]">Company Reg. No. <b className="text-[#0a2c4d]">16239546</b></div>
              </div>
            </div>
          </div>
          <div>
            <p className="eyebrow mb-3">OUR HISTORY</p>
            <h2 className="section-title text-4xl md:text-5xl mb-6">15 Years of Building Growth</h2>
            <p className="text-[#556a85] text-[16px] leading-relaxed mb-5">
              We started our journey over 15 years ago providing expert financial advice, but we quickly saw a
              bigger problem: founders and individuals were juggling disconnected consultants for their IT, wealth
              management, and brand growth.
            </p>
            <p className="text-[#556a85] text-[16px] leading-relaxed">
              What we found was a fragmented system that drained resources. After failing to find a single firm that
              could seamlessly merge IT innovation with corporate financial advisory, we realized the greater
              opportunity: building it ourselves. Today, with operational headquarters in Rome and our registered
              office in London, Midbrothers makes these comprehensive solutions available to innovators worldwide.
            </p>
          </div>
        </div>
      </section>

      {/* Founder Spotlight */}
      <section className="py-24 bg-white">
        <div className="max-w-[1180px] mx-auto px-6">
          <div className="text-center mb-14">
            <p className="eyebrow mb-3">OUR LEADERSHIP</p>
            <h2 className="section-title text-4xl md:text-5xl">The Architects of Growth</h2>
          </div>

          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex flex-col items-center mb-8">
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-[#0a6cb0] to-[#0a2c4d] text-white font-serif-display text-4xl font-bold flex items-center justify-center shadow-xl border-4 border-white ring-4 ring-[#0a6cb0]/10">
                SM
              </div>
              <p className="font-serif-display text-3xl font-bold text-[#0a2c4d] mt-5">Sayed Mid</p>
              <p className="text-[#0a6cb0] text-sm tracking-wider uppercase font-semibold">Founder &amp; CEO</p>
            </div>

            <h3 className="font-serif-display text-[#0a2c4d] text-3xl md:text-4xl font-bold mb-8 leading-tight">
              The Visionary Behind Midbrothers
            </h3>
            <p className="text-[#556a85] text-[16px] leading-relaxed mb-5 text-left md:text-center">
              Sayed Mid built Midbrothers on a singular, powerful premise: businesses should not have to choose
              between world-class financial strategy and cutting-edge technological infrastructure.
            </p>
            <p className="text-[#556a85] text-[16px] leading-relaxed mb-5 text-left md:text-center">
              With a deep understanding of global markets, investment strategies, and operational scaling, Sayed
              recognized that true growth happens when finance and technology operate in perfect synergy. Under
              his leadership, Midbrothers has evolved from an advisory firm into a global fintech ecosystem,
              empowering startups to secure funding, enterprise brands to launch robust digital platforms, and
              individuals to secure their financial futures.
            </p>
            <p className="text-[#556a85] text-[16px] leading-relaxed mb-10 text-left md:text-center">
              Sayed&apos;s vision continues to drive our mission to liberate global commerce and provide
              &quot;outside the box&quot; solutions for complex business challenges.
            </p>
            <blockquote className="border-l-4 border-[#0a6cb0] pl-6 py-2 font-serif-display text-[#0a2c4d] italic text-xl text-left max-w-2xl mx-auto">
              &ldquo;True growth happens when finance and technology operate in perfect synergy.&rdquo;
            </blockquote>
          </div>
        </div>
      </section>

      {/* Executive Team */}
      <section className="py-24 bg-[#f6f9fc]">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="text-center mb-14">
            <p className="eyebrow mb-3">THE EXECUTIVE TEAM</p>
            <h2 className="section-title text-4xl md:text-5xl">Meet Our Leadership</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TEAM.map((m) => {
              const initials = m.name.split(' ').map(w => w[0]).slice(0, 2).join('');
              return (
                <div key={m.name} className="bg-white rounded-2xl p-8 border border-[#eef1f5] shadow-[0_10px_30px_-20px_rgba(10,44,77,0.2)] hover:shadow-[0_18px_40px_-16px_rgba(10,108,176,0.3)] hover:-translate-y-1 transition-all text-center">
                  <div className="w-24 h-24 mx-auto mb-5 rounded-full bg-gradient-to-br from-[#0a6cb0] to-[#0a2c4d] text-white font-serif-display text-2xl font-bold flex items-center justify-center shadow-lg">
                    {initials}
                  </div>
                  <p className="font-serif-display text-xl font-bold text-[#0a2c4d]">{m.name}</p>
                  <p className="text-[#0a6cb0] text-xs uppercase tracking-wider font-semibold mb-3">{m.role}</p>
                  <p className="text-[#556a85] text-sm leading-relaxed">{m.bio}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="relative py-24 bg-[#0a2c4d] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a2c4d] via-[#0a2c4d]/95 to-[#0a3b6a]" />
        <div className="relative max-w-[1080px] mx-auto px-6 text-center">
          <p className="eyebrow text-white/70 mb-3">READY TO GROW?</p>
          <h2 className="font-serif-display text-white text-4xl md:text-5xl font-bold leading-tight mb-5">
            Let&apos;s Merge Our Business Goals
          </h2>
          <p className="text-white/80 text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            Join us as we innovate technology, secure futures, and scale startups around the globe.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/#contacts" className="pill-btn">
              CONTACT US <ArrowRight size={18} />
            </Link>
            <Link to="/#services" className="pill-btn pill-btn-outline" style={{ background: 'transparent', color: '#ffffff', borderColor: '#ffffff' }}>
              EXPLORE SOLUTIONS <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
